import React, { useState, useEffect } from 'react';
import { api } from '../App';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

function Ports() {
  const [ports, setPorts] = useState([]);
  const [filteredPorts, setFilteredPorts] = useState([]);
  const [selectedPort, setSelectedPort] = useState(null);
  const [portDetails, setPortDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Email functionality
  const [selectedHelpers, setSelectedHelpers] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    api('/admin/ports')
      .then(data => {
        setPorts(data.ports);
        setFilteredPorts(data.ports);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (search.trim() === '') {
      setFilteredPorts(ports);
    } else {
      const term = search.toLowerCase();
      setFilteredPorts(ports.filter(p =>
        p.name.toLowerCase().includes(term) ||
        (p.city && p.city.toLowerCase().includes(term)) ||
        (p.country && p.country.toLowerCase().includes(term))
      ));
    }
  }, [search, ports]);

  // Clear selection when port changes
  useEffect(() => {
    setSelectedHelpers([]);
  }, [selectedPort]);

  const loadPortDetails = async (portId) => {
    setSelectedPort(portId);
    setDetailsLoading(true);
    try {
      const data = await api(`/admin/ports/${portId}`);
      setPortDetails(data);
    } catch (err) {
      console.error(err);
      setPortDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const toggleSelectHelper = (helperId) => {
    setSelectedHelpers(prev =>
      prev.includes(helperId)
        ? prev.filter(id => id !== helperId)
        : [...prev, helperId]
    );
  };

  const toggleSelectAllHelpers = () => {
    if (!portDetails) return;
    if (selectedHelpers.length === portDetails.helpers.length) {
      setSelectedHelpers([]);
    } else {
      setSelectedHelpers(portDetails.helpers.map(h => h.id));
    }
  };

  const openEmailModal = () => {
    setEmailSubject('');
    setEmailBody('');
    setShowEmailModal(true);
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      alert('Please enter both subject and message');
      return;
    }

    setSendingEmail(true);
    try {
      const result = await api('/admin/users/email', {
        method: 'POST',
        body: JSON.stringify({
          userIds: selectedHelpers,
          subject: emailSubject,
          body: emailBody
        }),
      });
      alert(result.message);
      setShowEmailModal(false);
      setSelectedHelpers([]);
    } catch (err) {
      alert('Failed to send email: ' + err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const getSelectedHelpersList = () => {
    if (!portDetails) return [];
    return portDetails.helpers.filter(h => selectedHelpers.includes(h.id));
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading ports...</div>;

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Ports Analytics</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
        {/* Ports List */}
        <div className="card" style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}>
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search ports by name, city, country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <h3 style={{ marginBottom: 16 }}>Ports ({filteredPorts.length})</h3>
          {filteredPorts.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No ports found</p>
          ) : (
            filteredPorts.map((port) => (
              <div
                key={port.id}
                onClick={() => loadPortDetails(port.id)}
                style={{
                  padding: '12px 16px',
                  borderRadius: 8,
                  marginBottom: 8,
                  cursor: 'pointer',
                  background: selectedPort === port.id ? '#e3f2fd' : '#f8f9fa',
                  border: selectedPort === port.id ? '2px solid #0066cc' : '2px solid transparent',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{port.name}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{port.city}, {port.country}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: '#0066cc' }}>{port.total_requests}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>requests</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12 }}>
                  <span style={{ color: '#28a745' }}>{port.completed_requests} completed</span>
                  <span style={{ color: '#ffc107' }}>{port.active_requests} active</span>
                  <span style={{ color: '#6f42c1' }}>{port.helpers_available} helpers</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Port Details */}
        <div>
          {!selectedPort ? (
            <div className="card" style={{ padding: 60, textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⚓</div>
              <p>Select a port to view detailed analytics</p>
            </div>
          ) : detailsLoading ? (
            <div className="card" style={{ padding: 60, textAlign: 'center' }}>Loading port details...</div>
          ) : portDetails ? (
            <div>
              {/* Port Header */}
              <div className="card" style={{ marginBottom: 20 }}>
                <h2 style={{ marginBottom: 4 }}>{portDetails.port.name}</h2>
                <p style={{ color: '#666', marginBottom: 20 }}>{portDetails.port.city}, {portDetails.port.country}</p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#0066cc' }}>{portDetails.stats.total || 0}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>Total Requests</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#28a745' }}>{portDetails.stats.completed || 0}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>Completed</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#ffc107' }}>{(portDetails.stats.active || 0) + (portDetails.stats.matched || 0)}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>Active</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#17a2b8' }}>{portDetails.stats.pending || 0}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>Pending</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#dc3545' }}>{portDetails.stats.cancelled || 0}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>Cancelled</div>
                  </div>
                </div>
              </div>

              {/* Request Trends Chart */}
              {portDetails.requestTrends && portDetails.requestTrends.length > 0 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3 style={{ marginBottom: 16 }}>Request Trends (Last 30 Days)</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={portDetails.requestTrends.map(d => ({
                      ...d,
                      date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#0066cc" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Helpers at this Port */}
              <div className="card" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0 }}>Helpers at this Port ({portDetails.helpers.length})</h3>
                  {portDetails.helpers.length > 0 && (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      {selectedHelpers.length > 0 && (
                        <button
                          className="btn btn-primary"
                          onClick={openEmailModal}
                          style={{ padding: '6px 12px', fontSize: 13 }}
                        >
                          Email {selectedHelpers.length} Helper{selectedHelpers.length > 1 ? 's' : ''}
                        </button>
                      )}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={portDetails.helpers.length > 0 && selectedHelpers.length === portDetails.helpers.length}
                          onChange={toggleSelectAllHelpers}
                        />
                        Select All
                      </label>
                    </div>
                  )}
                </div>
                {portDetails.helpers.length === 0 ? (
                  <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No helpers registered for this port</p>
                ) : (
                  <div>
                    {portDetails.helpers.map(helper => (
                      <div key={helper.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        marginBottom: 8,
                        background: selectedHelpers.includes(helper.id) ? '#e3f2fd' : '#f8f9fa',
                        borderRadius: 8,
                        border: selectedHelpers.includes(helper.id) ? '2px solid #0066cc' : '2px solid transparent',
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedHelpers.includes(helper.id)}
                          onChange={() => toggleSelectHelper(helper.id)}
                          style={{ marginRight: 12, cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div style={{
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          borderRadius: '50%',
                          background: '#ddd',
                          backgroundImage: helper.profile_photo_url ? `url(${helper.profile_photo_url})` : 'none',
                          backgroundSize: 'cover',
                          marginRight: 12,
                          flexShrink: 0,
                        }} />
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {helper.first_name} {helper.last_name}
                          </div>
                          <div style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {helper.email}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: 12, flexShrink: 0 }}>
                          {helper.avg_rating ? (
                            <div style={{ color: '#f59e0b', fontWeight: 600, fontSize: 13 }}>
                              ★ {Number(helper.avg_rating).toFixed(1)} ({helper.rating_count})
                            </div>
                          ) : (
                            <div style={{ color: '#999', fontSize: 12 }}>No ratings</div>
                          )}
                        </div>
                        <span style={{
                          marginLeft: 12,
                          padding: '4px 10px',
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 500,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          background: helper.verification_status === 'approved' ? '#d4edda' : '#fff3cd',
                          color: helper.verification_status === 'approved' ? '#155724' : '#856404',
                        }}>
                          {helper.verification_status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Requests */}
              <div className="card">
                <h3 style={{ marginBottom: 16 }}>Recent Requests</h3>
                {portDetails.recentRequests.length === 0 ? (
                  <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No requests for this port</p>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Owner</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Helpers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {portDetails.recentRequests.map(req => (
                        <tr key={req.id}>
                          <td>{req.owner_first_name} {req.owner_last_name}</td>
                          <td>{req.scheduled_date || 'ASAP'}</td>
                          <td>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: 20,
                              fontSize: 12,
                              fontWeight: 500,
                              background: req.status === 'completed' ? '#cce5ff' : req.status === 'cancelled' ? '#f8d7da' : '#d4edda',
                              color: req.status === 'completed' ? '#004085' : req.status === 'cancelled' ? '#721c24' : '#155724',
                            }}>
                              {req.status}
                            </span>
                          </td>
                          <td>{req.helpers_confirmed || 0}/{req.helpers_needed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: 60, textAlign: 'center', color: '#dc3545' }}>
              Failed to load port details
            </div>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card" style={{ width: 600, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>Email Helpers at {portDetails?.port?.name}</h2>
              <button
                onClick={() => setShowEmailModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#666' }}
              >
                &times;
              </button>
            </div>

            {/* Recipients */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Recipients ({getSelectedHelpersList().length})
              </label>
              <div style={{
                background: '#f8f9fa',
                padding: 12,
                borderRadius: 8,
                maxHeight: 100,
                overflow: 'auto',
                fontSize: 13,
              }}>
                {getSelectedHelpersList().map((helper, i) => (
                  <span key={helper.id}>
                    {helper.first_name} {helper.last_name} &lt;{helper.email}&gt;
                    {i < getSelectedHelpersList().length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Subject</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject..."
                style={{ width: '100%' }}
              />
            </div>

            {/* Body */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Message
                <span style={{ fontWeight: 400, color: '#666', fontSize: 12, marginLeft: 8 }}>
                  (Use {'{first_name}'}, {'{last_name}'}, {'{email}'} for personalization)
                </span>
              </label>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Enter your message..."
                rows={8}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn"
                onClick={() => setShowEmailModal(false)}
                style={{ background: '#eee' }}
                disabled={sendingEmail}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={sendEmail}
                disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
              >
                {sendingEmail ? 'Sending...' : `Send to ${getSelectedHelpersList().length} Helper${getSelectedHelpersList().length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Ports;
